import React from "react";
/* Card — Phase B primitive (refreshed in Phase 2c Containers).
   Replaces legacy .card / .chart-card / .mdc / .fcard / .bell-panel / .dpop /
   .settings-menu / .inputs-panel.

   padding:    compact | default | comfortable | none  (default: default)
   elevation:  flat | raised  (default: raised)
   tone:       neutral (default) | accent | good | warn | danger | info
               Status tone applies a tone-colored border + soft background
               tint + title color. Useful for danger/warn callout sections
               without composing a Banner inside the card.
   as:         element name  (default: section) */

export type CardTone = "neutral" | "accent" | "good" | "warn" | "danger" | "info";

export interface CardProps extends React.HTMLAttributes<HTMLElement> {
  padding?: "compact" | "default" | "comfortable" | "none";
  elevation?: "flat" | "raised";
  tone?: CardTone;
  as?: keyof JSX.IntrinsicElements;
}

interface CardComponent extends React.FC<CardProps> {
  Header: React.FC<CardHeaderProps>;
  Body: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Footer: React.FC<React.HTMLAttributes<HTMLElement>>;
}

const Card: CardComponent = ({
  padding = "default",
  elevation = "raised",
  tone = "neutral",
  as = "section",
  className = "",
  children,
  ...rest
}) => {
  const Tag: any = as;
  const cls = [
    "v2-card",
    `v2-card--pad-${padding}`,
    `v2-card--elev-${elevation}`,
    // Skip the tone class for neutral — keeps the rendered HTML tidy for the
    // common case (most cards are neutral).
    tone !== "neutral" ? `v2-card--tone-${tone}` : "",
    className,
  ].filter(Boolean).join(" ");
  return <Tag className={cls} {...rest}>{children}</Tag>;
};

interface CardHeaderProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  controls?: React.ReactNode;
}

Card.Header = function CardHeader({ title, subtitle, controls, className = "", children, ...rest }: CardHeaderProps) {
  return (
    <header className={`v2-card__header ${className}`.trim()} {...rest}>
      <div className="v2-card__title-block">
        {title != null ? <h3 className="v2-card__title">{title}</h3> : null}
        {subtitle != null ? <div className="v2-card__subtitle">{subtitle}</div> : null}
      </div>
      {controls != null ? <div className="v2-card__controls">{controls}</div> : null}
      {children}
    </header>
  );
};

Card.Body = function CardBody({ className = "", children, ...rest }) {
  return <div className={`v2-card__body ${className}`.trim()} {...rest}>{children}</div>;
};

Card.Footer = function CardFooter({ className = "", children, ...rest }) {
  return <footer className={`v2-card__footer ${className}`.trim()} {...rest}>{children}</footer>;
};

export default Card;
